import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReportType =
  | 'driver_report'
  | 'auto_cancellation_rate'
  | 'auto_rapid_booking'
  | 'auto_post_accept_cancel';

export type ReportSeverity = 'low' | 'medium' | 'high';
export type BlockStatus = 'active' | 'lifted' | 'expired';
export type UnblockRequestStatus = 'none' | 'pending' | 'approved' | 'rejected';

export interface FraudReport {
  id: string;
  customer_id: string;
  customer?: { id: string; name: string; phone: string };
  reported_by?: string;
  reporter?: { id: string; name: string };
  booking_id?: string;
  report_type: ReportType;
  reason: string;
  severity: ReportSeverity;
  metadata?: Record<string, unknown>;
  is_resolved: boolean;
  resolved_by?: string;
  resolved_at?: string;
  created_at: string;
}

export interface UserBlock {
  id: string;
  customer_id: string;
  customer?: { id: string; name: string; phone: string };
  blocked_by?: string;
  reason: string;
  duration_days: number;
  blocked_until: string;
  status: BlockStatus;
  lifted_by?: string;
  lifted_at?: string;
  unblock_request_reason?: string;
  unblock_request_at?: string;
  unblock_request_status: UnblockRequestStatus;
  admin_notes?: string;
  created_at: string;
}

export interface FraudReportsQuery {
  page?: number;
  limit?: number;
  severity?: ReportSeverity | 'all';
  report_type?: ReportType | 'all';
  customer_id?: string;
  is_resolved?: boolean;
}

export interface UnblockRequestsQuery {
  page?: number;
  limit?: number;
  status?: 'pending' | 'approved' | 'rejected' | 'all';
}

export interface BlockCustomerPayload {
  reason: string;
  duration_days: 1 | 3 | 7 | 30;
}

export interface ProcessUnblockPayload {
  status: 'approved' | 'rejected';
  admin_notes?: string;
}

// ─── Driver Fraud Incident Types ─────────────────────────────────────────────

export type DriverFraudType = 'delivery_abandoned' | 'sla_breach' | 'gps_ghost';
export type DriverFraudStatus = 'suspected' | 'confirmed' | 'cleared';
export type DriverFraudDetectedBy = 'socket_disconnect' | 'sla_deadline' | 'gps_cron';

export interface DriverFraudIncident {
  id: string;
  booking_id: string;
  driver_id: string;
  driver?: { id: string; name: string; phone: string };
  booking?: { id: string; pickup_address: string; drop_address: string; estimated_fare: number };
  type: DriverFraudType;
  status: DriverFraudStatus;
  detected_by: DriverFraudDetectedBy;
  admin_notes?: string;
  resolved_by?: string;
  resolved_at?: string;
  detected_at: string;
}

export interface DriverRiskScore {
  driver_id: string;
  driver_name: string;
  driver_phone: string;
  total_trips: number;
  wallet_frozen: boolean;
  average_rating: number | null;
  risk_score: number;
  breakdown: {
    confirmed_incidents: number;
    suspected_incidents: number;
    gps_ghost_events: number;
    sla_breaches: number;
    disconnect_events: number;
  };
}

export interface FraudIntelligenceOverview {
  total_suspected: number;
  total_confirmed: number;
  total_cleared: number;
  flagged_active_deliveries: number;
  recent_incidents: DriverFraudIncident[];
}

export interface FlaggedBooking {
  id: string;
  pickup_address: string;
  drop_address: string;
  estimated_fare: number;
  flag_reason: string;
  flagged_at: string;
  driver?: { id: string; name: string; phone: string };
  customer?: { id: string; name: string; phone: string };
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const fraudApi = {
  getReports: async (query: FraudReportsQuery = {}): Promise<{ reports: FraudReport[]; total: number; page: number; pages: number }> => {
    const params: Record<string, unknown> = { page: query.page || 1, limit: query.limit || 20 };
    if (query.severity && query.severity !== 'all') params.severity = query.severity;
    if (query.report_type && query.report_type !== 'all') params.report_type = query.report_type;
    if (query.customer_id) params.customer_id = query.customer_id;
    if (query.is_resolved !== undefined) params.is_resolved = String(query.is_resolved);
    const res = await api.get(API_ENDPOINTS.FRAUD.REPORTS, { params });
    const raw = (res.data as any)?.data ?? res.data;
    return raw;
  },

  resolveReport: async (id: string): Promise<void> => {
    await api.put(API_ENDPOINTS.FRAUD.RESOLVE_REPORT(id));
  },

  getCustomerReports: async (customerId: string): Promise<{ reports: FraudReport[]; total: number }> => {
    const res = await api.get(API_ENDPOINTS.FRAUD.CUSTOMER_REPORTS(customerId));
    const raw = (res.data as any)?.data ?? res.data;
    return raw;
  },

  getBlockHistory: async (customerId: string): Promise<UserBlock[]> => {
    const res = await api.get(API_ENDPOINTS.FRAUD.CUSTOMER_BLOCKS(customerId));
    const raw = (res.data as any)?.data ?? res.data;
    return Array.isArray(raw) ? raw : raw.blocks ?? [];
  },

  blockCustomer: async (customerId: string, payload: BlockCustomerPayload): Promise<UserBlock> => {
    const res = await api.post(API_ENDPOINTS.FRAUD.BLOCK_CUSTOMER(customerId), payload);
    const raw = (res.data as any)?.data ?? res.data;
    return raw;
  },

  unblockCustomer: async (customerId: string): Promise<void> => {
    await api.put(API_ENDPOINTS.FRAUD.UNBLOCK_CUSTOMER(customerId));
  },

  getUnblockRequests: async (query: UnblockRequestsQuery = {}): Promise<{ blocks: UserBlock[]; total: number; page: number; pages: number }> => {
    const params: Record<string, unknown> = { page: query.page || 1, limit: query.limit || 20 };
    if (query.status && query.status !== 'all') params.status = query.status;
    const res = await api.get(API_ENDPOINTS.FRAUD.UNBLOCK_REQUESTS, { params });
    const raw = (res.data as any)?.data ?? res.data;
    return raw;
  },

  processUnblockRequest: async (blockId: string, payload: ProcessUnblockPayload): Promise<void> => {
    await api.put(API_ENDPOINTS.FRAUD.PROCESS_UNBLOCK(blockId), payload);
  },

  // ─── Intelligence ──────────────────────────────────────────────────────────

  getIntelligenceOverview: async (): Promise<FraudIntelligenceOverview> => {
    const res = await api.get(API_ENDPOINTS.FRAUD.INTELLIGENCE_OVERVIEW);
    return (res.data as any)?.data ?? res.data;
  },

  getDriverRiskScores: async (limit = 20): Promise<DriverRiskScore[]> => {
    const res = await api.get(API_ENDPOINTS.FRAUD.DRIVER_RISK, { params: { limit } });
    const raw = (res.data as any)?.data ?? res.data;
    return Array.isArray(raw) ? raw : [];
  },

  getFlaggedBookings: async (): Promise<FlaggedBooking[]> => {
    const res = await api.get(API_ENDPOINTS.FRAUD.FLAGGED_BOOKINGS);
    const raw = (res.data as any)?.data ?? res.data;
    return Array.isArray(raw) ? raw : [];
  },

  listDriverIncidents: async (filters: { status?: DriverFraudStatus; driver_id?: string; limit?: number; offset?: number } = {}): Promise<{ items: DriverFraudIncident[]; total: number }> => {
    const res = await api.get(API_ENDPOINTS.FRAUD.DRIVER_INCIDENTS, { params: filters });
    const raw = (res.data as any)?.data ?? res.data;
    return raw;
  },

  confirmDriverIncident: async (id: string, notes?: string): Promise<void> => {
    await api.post(API_ENDPOINTS.FRAUD.CONFIRM_DRIVER_INCIDENT(id), { notes });
  },

  clearDriverIncident: async (id: string, notes?: string): Promise<void> => {
    await api.post(API_ENDPOINTS.FRAUD.CLEAR_DRIVER_INCIDENT(id), { notes });
  },
};
